import {
  TAutoReplyBusinessHour,
  TAutoReplyBusinessHourMode,
  TAutoReplyOutsideHourMode,
  TAutoReplyWeekday,
} from 'api';
import { Button } from 'components/Button';
import { autoReplyMessageStrings } from 'constants/strings';
import { ToggleRow } from './ToggleRow';

type Props = {
  enabled: boolean;
  businessHours: TAutoReplyBusinessHour[];
  businessHourMode: TAutoReplyBusinessHourMode;
  outsideHourMode: TAutoReplyOutsideHourMode;
  isSaving: boolean;
  canSave?: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onBusinessHoursChange: (hours: TAutoReplyBusinessHour[]) => void;
  onBusinessHourModeChange: (mode: TAutoReplyBusinessHourMode) => void;
  onOutsideHourModeChange: (mode: TAutoReplyOutsideHourMode) => void;
  onSave: () => void;
};

export function ResponseTimeTab({
  enabled,
  businessHours,
  businessHourMode,
  outsideHourMode,
  isSaving,
  canSave = true,
  onEnabledChange,
  onBusinessHoursChange,
  onBusinessHourModeChange,
  onOutsideHourModeChange,
  onSave,
}: Props) {
  const updateBusinessHour = (
    weekday: TAutoReplyWeekday,
    updates: Partial<TAutoReplyBusinessHour>,
  ) => {
    onBusinessHoursChange(
      businessHours.map((item) =>
        item.weekday === weekday ? { ...item, ...updates } : item,
      ),
    );
  };

  return (
    <div className="grid gap-5">
      <ToggleRow
        checked={enabled}
        onChange={onEnabledChange}
        label={autoReplyMessageStrings.tabs.time}
      />
      <div className="grid gap-4 lg:grid-cols-[3fr_2fr] lg:items-start">
        <div className="grid gap-3 rounded-2xl border border-border/70 bg-white p-4">
          {businessHours.map((hour) => (
            <div
              key={hour.weekday}
              className="grid items-center gap-3 md:grid-cols-[80px_120px_1fr]"
            >
              <span className="font-semibold">
                {autoReplyMessageStrings.weekdays[hour.weekday]}
              </span>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={hour.closed}
                  onChange={(event) =>
                    updateBusinessHour(hour.weekday, {
                      closed: event.target.checked,
                    })
                  }
                />
                {autoReplyMessageStrings.businessHours.closedDay}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={hour.startTime ?? '09:00'}
                  disabled={hour.closed}
                  onChange={(event) =>
                    updateBusinessHour(hour.weekday, {
                      startTime: event.target.value,
                    })
                  }
                  className="rounded-lg border border-border px-3 py-2 text-sm"
                />
                <span>
                  {autoReplyMessageStrings.businessHours.timeRangeSeparator}
                </span>
                <input
                  type="time"
                  value={hour.endTime ?? '18:00'}
                  disabled={hour.closed}
                  onChange={(event) =>
                    updateBusinessHour(hour.weekday, {
                      endTime: event.target.value,
                    })
                  }
                  className="rounded-lg border border-border px-3 py-2 text-sm"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="grid gap-4 rounded-2xl border border-border/70 bg-white p-4">
          <label className="grid gap-2 text-sm font-semibold">
            {autoReplyMessageStrings.businessHours.duringHoursLabel}
            <select
              value={businessHourMode}
              onChange={(event) =>
                onBusinessHourModeChange(
                  event.target.value as TAutoReplyBusinessHourMode,
                )
              }
              className="rounded-xl border border-border p-3 font-normal"
            >
              <option value="KEYWORD_ONLY">
                {autoReplyMessageStrings.businessHourMode.KEYWORD_ONLY}
              </option>
              <option value="UNIFORM_AND_KEYWORD">
                {autoReplyMessageStrings.businessHourMode.UNIFORM_AND_KEYWORD}
              </option>
            </select>
          </label>
          <label className="grid gap-2 text-sm font-semibold">
            {autoReplyMessageStrings.businessHours.outsideHoursLabel}
            <select
              value={outsideHourMode}
              onChange={(event) =>
                onOutsideHourModeChange(
                  event.target.value as TAutoReplyOutsideHourMode,
                )
              }
              className="rounded-xl border border-border p-3 font-normal"
            >
              <option value="UNIFORM_ONLY">
                {autoReplyMessageStrings.outsideHourMode.UNIFORM_ONLY}
              </option>
              <option value="NO_REPLY">
                {autoReplyMessageStrings.outsideHourMode.NO_REPLY}
              </option>
            </select>
          </label>
        </div>
      </div>
      <div className="flex justify-end mx-4">
        <Button loading={isSaving} disabled={!canSave || isSaving} onClick={onSave}>
          {autoReplyMessageStrings.save}
        </Button>
      </div>
    </div>
  );
}
